import React, { useEffect, useRef } from "react";
import { ArrowRight, Download } from "lucide-react";

export default function Hero({ onRequestQuote, onDownloadCatalog }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Ensure muted is set as a property (React's `muted` attr sometimes lags).
    // Many browsers gate autoplay on the `muted` IDL property at play() time.
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    v.setAttribute("muted", "");
    v.setAttribute("autoplay", "");
    v.playbackRate = 0.55;
    v.defaultPlaybackRate = 0.55;

    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === "function") {
        p.catch(() => {
          // Autoplay was blocked — recover on the next user interaction.
          const resume = () => {
            v.muted = true;
            v.play().catch(() => {});
          };
          ["touchstart", "touchend", "click", "scroll"].forEach((evt) =>
            window.addEventListener(evt, resume, { once: true, passive: true })
          );
        });
      }
    };

    const apply = () => {
      v.muted = true;
      v.playbackRate = 0.55;
      tryPlay();
    };

    // Initial kick — load & try to play
    try { v.load(); } catch (_) {}
    apply();

    v.addEventListener("loadedmetadata", apply);
    v.addEventListener("loadeddata", apply);
    v.addEventListener("canplay", apply);
    v.addEventListener("play", () => {
      v.playbackRate = 0.55;
    });
    // If Safari pauses on tab change, resume on visibility return
    const onVis = () => {
      if (!document.hidden && v.paused) tryPlay();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      v.removeEventListener("loadedmetadata", apply);
      v.removeEventListener("loadeddata", apply);
      v.removeEventListener("canplay", apply);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative min-h-[100svh] overflow-hidden bg-[#0a1a17]"
    >
      {/* Background video */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          data-testid="hero-video"
          poster="/viewlux/video/hero-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/viewlux/video/hero.mp4" type="video/mp4" />
        </video>
        {/* Layered overlays for depth */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1a17]/80 via-[#0a1a17]/55 to-[#0a1a17]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(201,150,88,0.15),transparent_55%)]" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0a1a17] to-transparent" />
      </div>

      <div className="relative z-10 container-vlx min-h-[100svh] flex flex-col pt-28 md:pt-32 pb-20 md:pb-28">
        <div className="flex-1 flex flex-col justify-center max-w-4xl">
          <div className="flex items-center gap-4 mb-8">
            <span className="rule-gold" />
            <span className="text-[10.5px] tracking-[0.5em] uppercase text-[#e2b880] font-raleway">
              Colorado · Rocky Mountain Region
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="heading-xl text-white text-[46px] sm:text-[72px] md:text-[104px] leading-[0.95]"
          >
            Aluminum windows
            <br />
            and custom doors.
            <br />
            <span className="italic text-[#e2b880] font-light">
              Engineered to perform.
            </span>
          </h1>

          <p className="mt-10 max-w-xl text-white/75 text-[15px] md:text-[16px] font-nunito leading-[1.85]">
            ViewLux Colorado delivers premium thermally broken aluminum window
            and door systems engineered for Rocky Mountain climates — refined
            sightlines, insulated glass options for year-round indoor comfort,
            and uncompromising performance.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="hero-cta-quote"
              onClick={onRequestQuote}
              className="btn-pill btn-primary"
            >
              Request a Consultation
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="hero-cta-catalog"
              onClick={onDownloadCatalog}
              className="btn-pill btn-outline"
            >
              <Download className="w-3.5 h-3.5" />
              Download Catalog
            </button>
            <a
              href="#collection"
              data-testid="hero-cta-collection"
              className="hidden md:inline-flex link-arrow !text-[#e2b880] !border-[#e2b880]/40 hover:!text-white hover:!border-white"
            >
              Explore Collection
            </a>
          </div>
        </div>

        {/* Bottom catalog-style badge */}
        <div className="relative mt-auto flex items-end justify-between">
          <div className="hidden md:block">
            <div className="w-16 h-px bg-[#c99658] mb-4" />
            <p className="text-[10px] tracking-[0.48em] uppercase text-white/60 font-raleway">
              Scroll to experience
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
